import React, { Component } from 'react';
import ImageGallery, { FlowShot } from '../Imgpop';
import FeatureCards from '../FeatureCards';
import TechTags from '../TechTags';
import "./style.css"

class ObservationsDashboard extends Component {
    render() {
        const galleryImages = [
            {
                src: require('./img/observations/admin-observations-dash.png'),
                alt: 'Campus-wide Employee Observations Dashboard on a laptop',
                variant: 'laptop',
                className: 'laptop-view',
            },
            {
                src: require('./img/observations/teacher-dash.png'),
                alt: 'Personal teacher Observations Dashboard on a laptop',
                variant: 'laptop',
                className: 'laptop-view',
            },
            {
                src: require('./img/observations/active-campus.png'),
                title: 'Active campus',
                alt: 'Observations Dashboard greeting showing Chandler Campus as the active campus',
                variant: 'screenshot',
                className: 'screenshot-view',
            },
            {
                src: require('./img/observations/archive-control.png'),
                title: 'Archive control',
                alt: 'Archived observation drafts management table with Open Draft and Restore to Dashboard actions',
                variant: 'screenshot',
                className: 'screenshot-view',
            },
            {
                src: require('./img/observations/form.png'),
                title: 'Observation form',
                alt: 'Instructional Observations Form with domain checklists, ratings, and side navigation',
                variant: 'screenshot',
                className: 'screenshot-view',
            },
            {
                src: require('./img/observations/form-navigation.png'),
                title: 'Form navigation',
                alt: 'Observation form side navigation with nested headings, last-save status, and section progress',
                variant: 'screenshot',
                className: 'screenshot-view',
            },
            {
                src: require('./img/observations/offline.png'),
                title: 'Offline',
                alt: 'Observation form save bar showing Offline — Changes not being saved with backup download actions',
                variant: 'screenshot',
                className: 'screenshot-view',
            },
        ];

        const highlightCards = [
            {
                title: 'Personalized by Role',
                description: 'The dashboard automatically displays either a campus-wide administrative experience or an individual teacher experience based on the employee’s role and permissions.',
            },
            {
                title: 'Campus-Aware Data',
                description: 'Observation records, teachers, drafts, filters, and statistics are limited to the employee’s active campus and authorized observation types.',
            },
            {
                title: 'Complete Observation Lifecycle',
                description: 'Users can begin an observation, save progress, resume a draft, submit the completed form, review results, and update existing observations through one connected workflow.',
            },
        ];

        const experienceCards = [
            {
                title: 'Campus-Wide Dashboard',
                description: 'Authorized school leaders, campus managers, instructional coaches, and designated organizational roles receive a campus-wide view for monitoring and managing observation activity. Depending on their permissions, they can review completed observations, access drafts, monitor recent submissions, filter records, export data, and continue observation workflows for their active campus. Access can also be limited by observation type: for example, instructional coaches can use the campus dashboard while remaining restricted from Formal Observations.',
            },
            {
                title: 'Personal Teacher Dashboard',
                description: 'Teachers receive a simplified dashboard containing only observations in which they are the selected teacher. They can see their completed observation count, observation type, completion date, campus, subject, observer, and links to review completed results. Campus-wide statistics, other employees’ drafts, and administrative filtering tools remain hidden.',
            },
        ];

        const reportingItems = [
            'Observation-type selection',
            'Select All and Clear All controls',
            'Completed observation totals',
            'Draft and pending counts',
            'Activity from the previous seven days',
            'School-year selection',
            'Custom date-range filtering',
            'Teacher filtering',
            'Observer or “Completed By” filtering',
            'Subject filtering',
            'Recent submissions',
            'Pagination',
            'CSV export',
            'Links to view or edit authorized records',
        ];

        const observationStates = [
            {
                title: 'New',
                description: 'A new observation without an existing entry or saved draft.',
            },
            {
                title: 'Draft',
                description: 'An observation saved through Advanced Save & Continue that can be resumed later.',
            },
            {
                title: 'Completed',
                description: 'A submitted observation stored as a completed Gravity Forms entry.',
            },
            {
                title: 'Edit',
                description: 'An authorized update to an existing completed observation that preserves the original record.',
            },
        ];

        const formCapabilities = [
            'Smooth scrolling between form sections',
            'Active-section highlighting',
            'Nested navigation based on heading level',
            'Automatic updates after conditional logic changes',
            'Autosave status and last-saved time',
            'Offline and save-failure indicators',
            'Manual save controls',
            'Backup download when saving is unavailable',
            'A calculated score and performance level for Formal Observations',
        ];

        const editFlow = [
            'Open Existing Observation',
            'Verify Access',
            'Populate Form',
            'Validate Changes',
            'Update Original Entry',
            'Suppress Duplicate Notifications',
        ];

        const cacheFlow = [
            'Authenticated User',
            'Role and Campus Resolution',
            'Observation Query',
            'Cached Results',
            'Role-Specific Dashboard',
        ];

        const performanceCards = [
            {
                title: 'Asynchronous Dashboard Updates',
                description: 'Completed observations, drafts, recent submissions, statistics, subjects, and filter options are loaded through authenticated WordPress AJAX requests. Changing the school year retrieves the appropriate dataset, while most other filters operate on the already-loaded results for a faster experience.',
            },
            {
                title: 'Targeted Caching',
                description: 'Campus-wide results are cached according to the selected forms, campus, school year, and dashboard configuration. Frequently changing information uses shorter cache periods, while stable campus and taxonomy information can be retained longer.',
            },
            {
                title: 'Focused Invalidation',
                description: 'When an observation is submitted, updated, saved as a draft, archived, or restored, the system advances a shared data version. Cached results created before that change are treated as outdated without requiring broad deletion. The personal teacher dashboard uses a narrower cache of matching observation entry IDs so one teacher’s results can refresh without clearing every teacher dashboard.',
            },
        ];

        const notificationItems = [
            'Routes teacher notifications using the selected teacher’s email',
            'Prevents duplicate principal notifications when the same person would receive multiple copies',
            'Suppresses new-submission notifications while an existing observation is being edited',
            'Preserves the original observation record during authorized updates',
        ];

        const relatedProjects = [
            {
                id: 'legacy-link',
                title: 'Legacy Link Intranet Platform',
                description: 'The personalized employee intranet that provides the authentication, employee data, campus context, permissions, and platform foundation used by the Employee Observations Dashboard.',
                cta: 'View the Legacy Link Intranet Platform case study →',
            },
        ];

        return (
            <div className='collapsible'>
                <h1>Employee Observations Dashboard</h1>
                <div className="gradient"></div>
                <p className='eyebrow mt-4'>A role-aware system for managing instructional observations across campuses</p>
                <ImageGallery images={galleryImages}>
                    <div className="details">
                        <p>The Employee Observations Dashboard is a custom workflow built within Legacy Link to help school leaders, instructional coaches, and teachers manage instructional observations from one centralized location.</p>
                        <p>The system combines Gravity Forms observation data, saved drafts, campus assignments, employee permissions, and reporting tools into one personalized experience. Campus leaders receive broader operational visibility, while teachers receive a focused view of their own completed observations.</p>
                        <p className='eyebrow mt-4'>Dashboard at a Glance</p>
                        <FeatureCards className="mt-4 mb-4" items={highlightCards} />
                        <p className='eyebrow mt-4'>One System, Two Experiences</p>
                        <p>The dashboard uses a shared collection of forms, observation records, campus data, and helper services while providing two different experiences based on the authenticated employee.</p>
                        <FeatureCards className="mt-4 mb-4" items={experienceCards} />
                        <div className="row mt-3">
                            <div className='col-sm-6'>
                                <FlowShot {...galleryImages[0]} index={0} title="Campus Admin View" />
                            </div>
                            <div className='col-sm-6'>
                                <FlowShot {...galleryImages[1]} index={1} title="Teacher View" />
                            </div>
                        </div>
                        <p className='eyebrow mt-4'>Campus-Level Visibility and Reporting</p>
                        <p>The campus-wide dashboard brings multiple versions of Instructional Observations, Coaching Feedback, and Formal Observations into one consistent interface.</p>
                        <p>Related versions of an observation form can be grouped together so users see a single recognizable observation type while the system retrieves records from the appropriate underlying Gravity Forms forms.</p>
                        <p>The dashboard provides:</p>
                        <ul>
                            {reportingItems.map((item) => (
                                <li key={item}>{item}</li>
                            ))}
                        </ul>
                        <p>CSV exports reflect the user’s current filters, allowing campus teams to download the specific records they are reviewing rather than receiving an unfiltered data file.</p>
                        <p className='eyebrow mt-4'>Personalized by Campus and Permission</p>
                        <div className="row mt-3 align-items-center">
                            <div className='col-sm-5'>
                                <FlowShot {...galleryImages[2]} index={2} layout="single" />
                            </div>
                            <div className='col-sm-7'>
                                <p>The dashboard uses the employee’s authenticated account, assigned campus, active campus selection, role, and permissions to determine which experience and records they can access.</p>
                                <p>The active campus becomes the server-side boundary for observation queries. Even when information is requested asynchronously, the server independently confirms the employee’s identity, role, campus, and permitted observation types before returning results.</p>
                                <p>The personal teacher dashboard also verifies the current authenticated employee on the server rather than trusting identity information submitted by the browser. This prevents an employee from modifying a request to retrieve another teacher’s observations.</p>
                                <p className="status-label status-label-note">
                                    <span className="status-label-dot" aria-hidden="true"></span>
                                    Role, campus, and observation access are verified before records are returned.
                                </p>
                            </div>
                        </div>
                        <div className="feature-card">
                            <p className="eyebrow">Managing Drafts Without Losing Progress</p>
                            <p>Observations can be lengthy, so the system supports Gravity Perks Advanced Save &amp; Continue and clearly separates new forms, saved drafts, and completed-entry edits.</p>
                            <p>Saved drafts appear in the campus dashboard’s pending section, where authorized users can resume the form using its secure draft token. Draft ownership is preserved so another employee cannot unknowingly take over the original creator’s work.</p>
                            <p>I also created a non-destructive archive process for drafts. Archiving removes a draft from the active dashboard without immediately deleting its underlying Gravity Forms data. Authorized administrators can review and restore archived observation drafts through a separate management screen.</p>
                            <FlowShot {...galleryImages[3]} index={3} layout="single" />
                            <p className="eyebrow">Observation States</p>
                            <FeatureCards className="mt-4 mb-0" items={observationStates} />
                        </div>
                        <div className="feature-card">
                            <p className="eyebrow">A Guided Observation Form Experience</p>
                            <p>I developed a custom Gravity Form Navigation Menu widget to make long observation forms easier to navigate and complete.</p>
                            <p>The widget reads the headings within the active Gravity Form and turns them into a structured side navigation menu. Nested headings become nested navigation items, and the active section changes as the employee moves through the form.</p>
                            <p>The navigation responds to Gravity Forms conditional logic, preventing sections that are currently hidden from appearing as available navigation options.</p>
                            <div className="row mt-3">
                                <div className='col-sm-7'>
                                    <FlowShot {...galleryImages[4]} index={4} />
                                </div>
                                <div className='col-sm-5'>
                                    <FlowShot {...galleryImages[5]} index={5} />
                                </div>
                            </div>
                            <p>Additional functionality includes:</p>
                            <ul>
                                {formCapabilities.map((item) => (
                                    <li key={item}>{item}</li>
                                ))}
                            </ul>
                            <div className="row mt-3 align-items-center">
                                <div className='col-sm-5'>
                                    <FlowShot {...galleryImages[6]} index={6} layout="single" />
                                </div>
                                <div className='col-sm-7'>
                                    <p>If connectivity is interrupted or a save fails, the user can download a local backup of visible form information as a PDF, with a text-file fallback when PDF generation is unavailable.</p>
                                </div>
                            </div>
                            <p className='eyebrow mt-4'>Editing Completed Observations</p>
                            <p>Editing an existing observation requires a different process from resuming a saved draft.</p>
                            <p>When an authorized user opens a completed observation, the system retrieves the original Gravity Forms entry and prepopulates the current form with its saved values. Checkbox, radio, and conditional selections are restored after the form finishes rendering.</p>
                            <p>The updated submission passes through the normal Gravity Forms validation process. The system then copies the approved changes back to the original observation, removes the temporary entry created during submission, and suppresses notifications intended only for new observations.</p>
                            <p>This keeps the original entry as the source of truth while preserving Gravity Forms validation and conditional behavior.</p>
                            <ol className="sync-diagram" aria-label="Completed observation edit workflow">
                                {editFlow.map((step) => (
                                    <li key={step} className="sync-diagram-step">
                                        <span className="sync-diagram-card">{step}</span>
                                    </li>
                                ))}
                            </ol>
                            <p className='eyebrow mt-4'>Automated Teacher Selection</p>
                            <p>Observation forms include a dynamic teacher selector populated according to the employee’s active campus.</p>
                            <p>The system retrieves only employees assigned to the selected campus and an eligible employee group, including teachers, specialists, paraprofessionals, interventionists, special education employees, speech-language pathologists, and substitutes.</p>
                            <p>The same approved teacher list is used while the form is rendered, validated, submitted, or updated asynchronously. This keeps the available choices consistent throughout the complete form lifecycle.</p>
                            <p>Teacher lists are cached by campus and refreshed when relevant employee profiles, campus assignments, or employee-group assignments change.</p>
                        <p className='eyebrow mt-4'>Efficient Data Loading</p>
                        <FeatureCards className="mt-4 mb-4" items={performanceCards} />
                        <ol className="sync-diagram" aria-label="Observation dashboard data loading flow">
                            {cacheFlow.map((step) => (
                                <li key={step} className="sync-diagram-step">
                                    <span className="sync-diagram-card">{step}</span>
                                </li>
                            ))}
                        </ol>
                        </div>
                        <p className='eyebrow mt-4'>Notifications and Workflow Controls</p>
                        <p>Gravity Forms manages notifications for newly completed observations, while custom logic adjusts delivery according to the observation workflow.</p>
                        <p>The system:</p>
                        <ul>
                            {notificationItems.map((item) => (
                                <li key={item}>{item}</li>
                            ))}
                        </ul>
                        <p>These controls help ensure that the right employees receive the appropriate information without duplicate or misleading messages.</p>
                        <p className="eyebrow mt-4">My Role</p>
                        <p>I designed and developed the Employee Observations Dashboard as a custom experience within Legacy Link.</p>
                        <p>My work included planning the role-dependent dashboard architecture, connecting Gravity Forms entries and drafts, creating the campus-wide and personal teacher views, building the filtering and reporting tools, developing the observation form navigation, implementing draft and completed-entry workflows, optimizing database queries, adding cache and invalidation strategies, and securing asynchronous requests.</p>
                        <p>I worked with school and organizational stakeholders to translate the instructional observation process into a practical system that supports both campus-wide oversight and individual teacher access.</p>
                        <TechTags
                            items={['PHP', 'JavaScript', 'SQL', 'WordPress', 'Gravity Forms', 'Gravity Perks Advanced Save & Continue', 'WordPress AJAX API', 'Gravity Forms API', 'WordPress Roles and Capabilities', 'Custom Taxonomies', 'MySQL', 'Transients and Caching', 'HTML', 'CSS', 'Bootstrap', 'jsPDF']}
                        />
                        <p className="eyebrow mt-4">Related Project</p>
                        <FeatureCards className="mt-4 mb-4" items={relatedProjects} onNavigate={this.props.onNavigate} />
                    </div>
                </ImageGallery>
            </div>
        )
    }
}

export default ObservationsDashboard;
